import { FC, ReactElement, useEffect, useState } from 'react';

import classNames from 'classnames';

import {
	EvIcon,
	EvSpinner,
	PlatformNoPreviewImage,
	ScreenshotBoundingBox
} from '@evinced-private/ui-common';

import { drawMobileCroppedIssue } from 'src/common/components/mobile-screenshot-cell-formatter/MobileScreenshotCellFormatterHelper';
import { Logger } from 'src/common/services/Logger';
import { ScreenshotDetails } from 'src/common/services/ScreenshotService';

import './MobileScreenshotCellFormatter.scss';

interface IMobileScreenshotCellFormatterProps {
	cellDescriptor: string;
	screenshotDetails: ScreenshotDetails;
	imageContainerClassName?: string;
	screenshotImgClassName?: string;
	viewportWidth: number;
	viewportHeight: number;
}

export const MobileScreenshotCellFormatter: FC<IMobileScreenshotCellFormatterProps> = ({
	screenshotDetails,
	cellDescriptor,
	imageContainerClassName,
	screenshotImgClassName,
	viewportWidth,
	viewportHeight
}) => {
	const [isLoading, setLoading] = useState(true);
	const [image, setImage] = useState<string | null>(null);

	useEffect(() => {
		const loadImage = async (): Promise<void> => {
			try {
				const croppedImageUrl = await drawMobileCroppedIssue({
					coords: screenshotDetails.boundingBoxes[0] as ScreenshotBoundingBox,
					rootBounds: { x: 0, y: 0, width: viewportWidth, height: viewportHeight },
					screenshotUrl: screenshotDetails.pageScreenshotUrl
				});

				setImage(croppedImageUrl);
			} catch (e) {
				Logger.warn('Failed to generate mobile screenshot:', e);
				setImage(screenshotDetails?.pageScreenshotUrl || null);
			} finally {
				setLoading(false);
			}
		};

		loadImage();
	}, [screenshotDetails, viewportWidth, viewportHeight]);

	const renderImagePlaceholder = (): ReactElement => {
		return (
			<EvIcon
				icon={PlatformNoPreviewImage}
				title="not available"
				className="preview-not-available"
			/>
		);
	};

	const renderImageButton = (): ReactElement => {
		if (!image) {
			return renderImagePlaceholder();
		}

		return (
			<img
				src={image}
				alt={`${cellDescriptor} issue screenshot`}
				aria-label={`${cellDescriptor} issue details`}
				className={classNames('mobile-screenshot-cell-img', {
					[screenshotImgClassName]: !!screenshotImgClassName
				})}
			/>
		);
	};

	return (
		<div className="mobile-screenshot-cell">
			<div
				className={classNames('image-container', {
					[imageContainerClassName]: !!imageContainerClassName
				})}
			>
				{isLoading ? <EvSpinner /> : renderImageButton()}
			</div>
		</div>
	);
};
